import { memo, useEffect, useRef, useState } from "react"
import { Form, Formik, useFormikContext } from "formik"
import * as yup from "yup"
import { Box, MenuItem, Popover, Tooltip, Typography, ToggleButton, ToggleButtonGroup } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleInfo } from "@fortawesome/pro-light-svg-icons"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { LocalizedEnum } from "../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { StandardLoadingButton } from "../../../../../../shared/components/buttons/LoadingButton"
import { FormikDropdown, FormikTextField } from "../../../../../../shared/components/forms/Formik"
import { iterateStringEnum } from "../../../../../../lib/enums"
import { sanitizeAndLimitInput } from "../../../../../../shared/components/createOrder/components/form/helpers"
import { INVALID_INPUT_KEY } from "../../../../../nonAdminUser/reports/settings/wallets/refill/components/WalletsRefillManual"
import {
    ProductStockInfo,
    StockAdjustmentType,
    StockChangeReason,
    StockPopoverFormValues,
    ItemsPerDayFormValues,
    UpdateStockPayload,
    INBOUND_REASONS,
    OUTBOUND_REASONS,
    ADJUSTMENT_TO_TRANSACTION,
} from "../../productsTypes"

const useStyles = makeStyles()((theme) => ({
    content: {
        padding: "2rem",
        display: "flex",
        flexDirection: "column",
        gap: "3rem",
        width: "28rem",
    },
    toggleContainer: {
        borderRadius: "5.625rem",
        backgroundColor: theme.palette.tailwind.slate[100],
    },
    toggleGroup: {
        gap: "0.25rem",
        "& .MuiToggleButtonGroup-grouped:not(:last-of-type), & .MuiToggleButtonGroup-grouped:not(:first-of-type)": {
            borderRadius: "5.625rem",
        },
    },
    toggleButton: {
        textTransform: "none",
        border: "none",
        padding: "0.1rem 2rem",
        fontWeight: 400,
        fontSize: "1rem",
        whiteSpace: "nowrap",
        color: theme.palette.text.secondary,
        background: "transparent",
        "&.Mui-selected, &.Mui-selected:hover": {
            fontWeight: 500,
            backgroundColor: "#CBD5E1",
            color: theme.palette.text.primary,
        },
        "&:hover": {
            background: "transparent",
        },
    },
    fieldGroup: {
        display: "flex",
        flexDirection: "column",
        gap: "0.5rem",
    },
    labelRow: {
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
    },
    inputField: {
        maxWidth: "10rem",
        "& .MuiInputBase-root": {
            height: "2.5rem",
        },
    },
    inputFieldSmall: {
        maxWidth: "6rem",
        "& .MuiInputBase-root": {
            height: "2.5rem",
        },
    },
    reasonField: {
        width: "100%",
        height: "2.5rem",
    },
    noteField: {
        "& .MuiInputBase-root": {
            padding: "8.5px 14px",
        },
    },
    infoIcon: {
        fontSize: "1rem",
        color: theme.palette.text.secondary,
    },
    confirmButton: {
        marginTop: "1.5rem",
        paddingLeft: "2rem",
        paddingRight: "2rem",
    },
}))

type StockTab = "inventory" | "daily"

type StockPopoverProps = Readonly<{
    anchorEl: HTMLElement | null
    onClose: () => void
    productId: number
    currentStock: ProductStockInfo
    onStockUpdate: (payload: UpdateStockPayload) => void
    formMode?: boolean
    initialInventoryValues?: StockPopoverFormValues
    initialDailyValues?: ItemsPerDayFormValues
    onInventoryValuesChange?: (values: StockPopoverFormValues) => void
}>

export const StockPopover = memo(function StockPopover({
    anchorEl,
    onClose,
    productId,
    currentStock,
    onStockUpdate,
    formMode,
    initialInventoryValues,
    initialDailyValues,
    onInventoryValuesChange,
}: StockPopoverProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const [activeTab, setActiveTab] = useState<StockTab>("inventory")

    const handleTabChange = (_event: React.MouseEvent<HTMLElement>, newValue: StockTab | null) => {
        if (newValue !== null) {
            setActiveTab(newValue)
        }
    }

    const inventoryValidationSchema = yup.object({
        type: yup.string().required(l10n.getString("required-field")),
        quantityDelta: yup.number().required(l10n.getString("required-field")).min(1),
        reason: yup.string().required(l10n.getString("required-field")),
        note: yup.string().max(NOTE_MAX_LENGTH),
    })

    const dailyValidationSchema = yup.object({
        itemsForSale: yup.number().required(l10n.getString("required-field")).min(0),
    })

    const inventoryInitialValues: StockPopoverFormValues = initialInventoryValues ?? {
        type: StockAdjustmentType.INBOUND,
        quantityDelta: "",
        reason: "",
        note: "",
    }

    const dailyInitialValues: ItemsPerDayFormValues = initialDailyValues ?? {
        itemsForSale: currentStock.sales?.daily?.itemsForSale?.toString() ?? "",
    }

    async function handleInventorySubmit(values: StockPopoverFormValues) {
        const qty = Number(values.quantityDelta)
        if (values.quantityDelta && !isNaN(qty) && values.reason) {
            await onStockUpdate({
                productId,
                inventory: {
                    stockUpdate: {
                        transactionType: ADJUSTMENT_TO_TRANSACTION[values.type],
                        quantityDelta: qty,
                        reason: values.reason as StockChangeReason,
                        note: values.note || undefined,
                    },
                },
            })
            onClose()
        }
    }

    async function handleDailySubmit(values: ItemsPerDayFormValues) {
        const items = Number(values.itemsForSale)
        if (values.itemsForSale && !isNaN(items)) {
            await onStockUpdate({
                productId,
                sales: {
                    daily: {
                        itemsForSale: items,
                    },
                },
            })
            onClose()
        }
    }

    return (
        <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
            <Box className={classes.content}>
                {!formMode && (
                    <Box className={classes.toggleContainer}>
                        <ToggleButtonGroup
                            value={activeTab}
                            exclusive
                            onChange={handleTabChange}
                            className={classes.toggleGroup}
                            fullWidth
                        >
                            <ToggleButton value="inventory" className={classes.toggleButton} disableRipple>
                                <LocalizedStrict id="product-stock-tab-inventory">
                                    <span>Inventory</span>
                                </LocalizedStrict>
                            </ToggleButton>
                            <ToggleButton value="daily" className={classes.toggleButton} disableRipple>
                                <LocalizedStrict id="product-stock-tab-daily">
                                    <span>Daily</span>
                                </LocalizedStrict>
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                )}

                {formMode ? (
                    <Formik<StockPopoverFormValues>
                        initialValues={inventoryInitialValues}
                        onSubmit={() => {}}
                        validateOnBlur={false}
                        validateOnChange={false}
                    >
                        <>
                            <InventoryFormContent formMode />
                            {onInventoryValuesChange && (
                                <FormikValuesSync<StockPopoverFormValues> onChange={onInventoryValuesChange} />
                            )}
                        </>
                    </Formik>
                ) : (
                    <>
                        {activeTab === "inventory" && (
                            <Formik<StockPopoverFormValues>
                                initialValues={inventoryInitialValues}
                                validationSchema={inventoryValidationSchema}
                                onSubmit={handleInventorySubmit}
                                validateOnBlur={false}
                                validateOnChange={false}
                            >
                                <Form>
                                    <InventoryFormContent currentStockTotal={currentStock.inventory.total} />
                                </Form>
                            </Formik>
                        )}
                        {activeTab === "daily" && (
                            <Formik<ItemsPerDayFormValues>
                                initialValues={dailyInitialValues}
                                validationSchema={dailyValidationSchema}
                                onSubmit={handleDailySubmit}
                                validateOnBlur={false}
                                validateOnChange={false}
                            >
                                <Form>
                                    <ItemsPerDayFormContent />
                                </Form>
                            </Formik>
                        )}
                    </>
                )}
            </Box>
        </Popover>
    )
})

function FormikValuesSync<T>({ onChange }: { onChange: (values: T) => void }) {
    const { values } = useFormikContext<T>()
    const onChangeRef = useRef(onChange)
    onChangeRef.current = onChange

    useEffect(() => {
        onChangeRef.current(values)
    }, [values])

    return null
}

const NOTE_MAX_LENGTH = 200

function InventoryFormContent({ formMode, currentStockTotal }: { formMode?: boolean; currentStockTotal?: number }) {
    const { classes } = useStyles()
    const { values, setFieldValue, isSubmitting } = useFormikContext<StockPopoverFormValues>()

    const reasons = values.type === StockAdjustmentType.INBOUND ? INBOUND_REASONS : OUTBOUND_REASONS

    const handleTypeChange = () => {
        setFieldValue("reason", "")
    }

    return (
        <Box display="flex" flexDirection="column" gap="1.5rem">
            {currentStockTotal != null && (
                <Box display="flex" alignItems="center" gap="0.5rem">
                    <LocalizedStrict id="product-stock-current-stock-label">
                        <Typography variant="body1" fontWeight={500}>
                            Current stock:
                        </Typography>
                    </LocalizedStrict>
                    <Typography variant="body1" fontWeight={400}>
                        {currentStockTotal}
                    </Typography>
                </Box>
            )}

            <Box className={classes.fieldGroup}>
                <Box className={classes.labelRow}>
                    <LocalizedStrict id="product-stock-type-label">
                        <Typography variant="body1" color="textPrimary" fontWeight={500}>
                            Type:
                        </Typography>
                    </LocalizedStrict>
                </Box>
                <FormikDropdown name="type" fullWidth className={classes.reasonField} onChange={handleTypeChange}>
                    {[...iterateStringEnum(StockAdjustmentType)].map((type) => (
                        <MenuItem key={type} value={type}>
                            <LocalizedEnum base="product-stock-type-option" enumValue={type}>
                                <span>{type}</span>
                            </LocalizedEnum>
                        </MenuItem>
                    ))}
                </FormikDropdown>
            </Box>

            <Box className={classes.fieldGroup}>
                <Box className={classes.labelRow}>
                    <LocalizedStrict id="product-stock-adjustment-amount-label">
                        <Typography variant="body1" color="textPrimary" fontWeight={500}>
                            Quantity:
                        </Typography>
                    </LocalizedStrict>
                    <LocalizedStrict id="product-stock-adjustment-amount-tooltip" attrs={{ title: true }}>
                        <Tooltip title="Enter the quantity to adjust">
                            <FontAwesomeIcon icon={faCircleInfo} className={classes.infoIcon} />
                        </Tooltip>
                    </LocalizedStrict>
                </Box>
                <FormikTextField
                    name="quantityDelta"
                    type="number"
                    className={classes.inputField}
                    inputProps={{
                        inputMode: "numeric" as const,
                        onInput: (e: React.FormEvent<HTMLInputElement>) => {
                            const sanitized = sanitizeAndLimitInput(e.currentTarget.value, 6)
                            if (e.currentTarget.value !== sanitized) {
                                e.currentTarget.value = sanitized
                            }
                        },
                        onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                            if (INVALID_INPUT_KEY.includes(e.key)) {
                                e.preventDefault()
                            }
                        },
                    }}
                />
            </Box>

            <Box className={classes.fieldGroup}>
                <Box className={classes.labelRow}>
                    <LocalizedStrict id="product-stock-reason-label">
                        <Typography variant="body1" color="textPrimary" fontWeight={500}>
                            Reason:
                        </Typography>
                    </LocalizedStrict>
                    <LocalizedStrict id="product-stock-reason-tooltip" attrs={{ title: true }}>
                        <Tooltip title="Select the reason for this stock adjustment">
                            <FontAwesomeIcon icon={faCircleInfo} className={classes.infoIcon} />
                        </Tooltip>
                    </LocalizedStrict>
                </Box>
                <FormikDropdown name="reason" fullWidth className={classes.reasonField}>
                    {reasons.map((reason) => (
                        <MenuItem key={reason} value={reason}>
                            <LocalizedEnum
                                base="product-stock-reason-tooltip"
                                enumValue={reason}
                                attrs={{ title: true }}
                            >
                                <Tooltip title="reason tooltip" placement="right">
                                    <Box width="100%">
                                        <LocalizedEnum base="product-stock-reason-option" enumValue={reason}>
                                            <span>{reason}</span>
                                        </LocalizedEnum>
                                    </Box>
                                </Tooltip>
                            </LocalizedEnum>
                        </MenuItem>
                    ))}
                </FormikDropdown>
            </Box>

            <Box className={classes.fieldGroup}>
                <Box className={classes.labelRow}>
                    <LocalizedStrict id="product-stock-note-label">
                        <Typography variant="body1" color="textPrimary" fontWeight={500}>
                            Note:
                        </Typography>
                    </LocalizedStrict>
                </Box>
                <LocalizedStrict id="product-stock-note-placeholder" attrs={{ placeholder: true }}>
                    <FormikTextField
                        name="note"
                        fullWidth
                        multiline
                        minRows={1}
                        maxRows={3}
                        placeholder="Optional extra info about adjustment"
                        className={classes.noteField}
                        inputProps={{ maxLength: NOTE_MAX_LENGTH }}
                    />
                </LocalizedStrict>
            </Box>

            {!formMode && (
                <Box display="flex" justifyContent="flex-end">
                    <LocalizedStrict id="product-stock-confirm-button">
                        <StandardLoadingButton
                            loading={isSubmitting}
                            fullWidth={false}
                            type="submit"
                            size="medium"
                            updateDefaultClasses={classes.confirmButton}
                        >
                            Confirm
                        </StandardLoadingButton>
                    </LocalizedStrict>
                </Box>
            )}
        </Box>
    )
}

function ItemsPerDayFormContent() {
    const { classes } = useStyles()
    const { isSubmitting } = useFormikContext<ItemsPerDayFormValues>()

    return (
        <Box display="flex" flexDirection="column" gap="1.5rem">
            <Box className={classes.fieldGroup}>
                <Box className={classes.labelRow}>
                    <LocalizedStrict id="product-stock-items-per-day-label">
                        <Typography variant="body1" color="textPrimary" fontWeight={500}>
                            Items per day
                        </Typography>
                    </LocalizedStrict>
                    <LocalizedStrict id="product-stock-on-sale-per-day-info" attrs={{ title: true }}>
                        <Tooltip title="Using this you can limit the number of items on sale each day.">
                            <FontAwesomeIcon icon={faCircleInfo} className={classes.infoIcon} />
                        </Tooltip>
                    </LocalizedStrict>
                </Box>
                <FormikTextField
                    name="itemsForSale"
                    type="number"
                    className={classes.inputFieldSmall}
                    inputProps={{
                        min: 0,
                        inputMode: "numeric" as const,
                        onInput: (e: React.FormEvent<HTMLInputElement>) => {
                            const sanitized = sanitizeAndLimitInput(e.currentTarget.value, 6)
                            if (e.currentTarget.value !== sanitized) {
                                e.currentTarget.value = sanitized
                            }
                        },
                        onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                            if (INVALID_INPUT_KEY.includes(e.key)) {
                                e.preventDefault()
                            }
                        },
                    }}
                />
            </Box>
            <Box display="flex" justifyContent="flex-end">
                <LocalizedStrict id="product-stock-confirm-button">
                    <StandardLoadingButton
                        loading={isSubmitting}
                        fullWidth={false}
                        type="submit"
                        size="medium"
                        updateDefaultClasses={classes.confirmButton}
                    >
                        Confirm
                    </StandardLoadingButton>
                </LocalizedStrict>
            </Box>
        </Box>
    )
}
